import {useContext, useEffect, useMemo, useState} from 'react';
import styles from './registered-tab.module.scss';
import {createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, useReactTable} from '@tanstack/react-table';
import IconButton from 'src/autobahn/components/button/icon-button';
import classNames from 'classnames';
import Button from 'src/autobahn/components/button/button';
import SearchInput from 'src/autobahn/components/search-input';
import {MaintenanceDispatchContext} from './contexts';
import {useDeleteMaintenance, useMaintenances} from './queries';
import Maintenance from 'src/core/models/maintenance';
import LinkDevice from './link-devices';
import TablePageControls from 'src/autobahn/components/table-page-controls';
import Table from 'src/autobahn/components/table';
import MaintenanceEditForm from './edit-form';
import Dialog from 'src/autobahn/components/dialog';
import {useTablePagination} from 'src/autobahn/hooks/useTablePagination';
import {formatDate} from 'src/helpers/date';
import useTimezone from 'src/hooks/use-timezone';
import {useIntl} from 'react-intl';
import {ThemeContext} from 'src/apiContexts';

type RegisteredMaintenance = {
  id: number;
  title: string;
  hourmeterValue?: number;
  odometerValue?: number;
  dateValue?: string;
  intervalValue?: number;
}

const columnHelper = createColumnHelper<RegisteredMaintenance>();

const MaintenanceRegisteredTab = () => {
  const dispatch = useContext(MaintenanceDispatchContext);
  const [activeAction, setActiveAction] = useState<null | 'link' | 'edit' | 'delete'>(null);
  const [selectedMaintenance, setSelectedMaintenance] = useState<number | null>(null);
  const [filterText, setFilterText] = useState('');

  const {timezone} = useTimezone();

  const intl = useIntl();
  const theme = useContext(ThemeContext);

  const isDeleteActionActive = activeAction === 'delete' && selectedMaintenance !== null;

  const {mutate: deleteMaintenance} = useDeleteMaintenance();

  const setActiveActionToLink = (selectedMaintenance: number) => {
    setSelectedMaintenance(selectedMaintenance);
    setActiveAction('link');
  };

  const setActiveActionToEdit = (selectedMaintenance: number) => {
    setSelectedMaintenance(selectedMaintenance);
    setActiveAction('edit');
  };

  const setActiveActionToDelete = (selectedMaintenance: number) => {
    setSelectedMaintenance(selectedMaintenance);
    setActiveAction('delete');
  };

  const dismissActiveAction = () => {
    setActiveAction(null);
    setSelectedMaintenance(null);
  };

  const onConfirmDelete = () => {
    deleteMaintenance(selectedMaintenance!, {
      onSuccess: () => {
        dismissActiveAction();
      },
    });
  };

  const columns = useMemo(() => [
      columnHelper.accessor('title', {
      header: intl.formatMessage({id: 'sharedTitle'}),
      cell: (info) => info.getValue(),
      meta: {
        className: styles.cellVerticalAlignBaseline,
      },
    }),
    columnHelper.display({
      id: 'kind',
      header: intl.formatMessage({id: 'sharedKind'}),
      cell: (info) => {
        const rowValue = info.row.original;

        return (
          <div className={styles.kindAndPeriodCell}>
            {!!rowValue.odometerValue && <span>{intl.formatMessage({id: 'sharedOdometer'})}</span>}
            {!!rowValue.dateValue && <span>{intl.formatMessage({id: 'sharedDate'})}</span>}
            {!!rowValue.hourmeterValue && <span>{intl.formatMessage({id: 'sharedHourmeter'})}</span>}
            {!!rowValue.intervalValue && <span>{intl.formatMessage({id: 'sharedInterval'})}</span>}
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'period',
      header: intl.formatMessage({id: 'sharedPeriod'}),
      cell: (info) => {
        const rowValue = info.row.original;

        return (
          <div className={styles.kindAndPeriodCell}>
            {!!rowValue.odometerValue && <span>{rowValue.odometerValue}</span>}
            {!!rowValue.dateValue && <span>{rowValue.dateValue}</span>}
            {!!rowValue.hourmeterValue && <span>{rowValue.hourmeterValue}</span>}
            {!!rowValue.intervalValue && <span>{rowValue.intervalValue}</span>}
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'action',
      header: intl.formatMessage({id: 'sharedAction'}),
      cell: ({row}) => (
        <div className={styles.actionButtons}>
          <IconButton
            onClick={() => {
              setActiveActionToEdit(row.original.id);
            }}
            label={intl.formatMessage({id: 'sharedEdit'})}
            className={classNames(styles.actionButton, styles.editButton)}
          >
            <i className='fas fa-edit' />
          </IconButton>
          <IconButton
            onClick={() => {
              setActiveActionToLink(row.original.id);
            }}
            label='Link'
            className={classNames(styles.actionButton, styles.linkButton)}
          >
            <i className='fas fa-link' />
          </IconButton>
          <IconButton
            onClick={() => {
              setActiveActionToDelete(row.original.id);
            }}
            label={intl.formatMessage({id: 'sharedRemove'})}
            className={classNames(styles.actionButton, styles.deleteButton)}
          >
            <i className='fas fa-trash' />
          </IconButton>
        </div>
      ),
      meta: {
        className: styles.cellVerticalAlignBaseline,
      },
    }),
  ], []);

  const {data: maintenances} = useMaintenances();

  const data = useMemo(() => {
    return maintenances.map((maintenance: Maintenance) => ({
      id: maintenance.id,
      title: maintenance.name,
      hourmeterValue: maintenance.hourmeterValue,
      odometerValue: maintenance.odometerValue,
      dateValue: maintenance.dateValue ? formatDate(maintenance.dateValue, intl.formatMessage({id: 'dateFormat'}), timezone) : undefined,
      intervalValue: maintenance.intervalValue,
    }));
  }, [maintenances, timezone]);

  const {pagination, currentPageSize, setPagination} = useTablePagination();

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setFilterText,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    globalFilterFn: 'includesString',
    state: {
      pagination: {
        ...pagination,
        pageSize: currentPageSize,
      },
      globalFilter: filterText,
    },
  });

  const setActiveViewToCreateMaintenance = () => {
    dispatch({type: 'SET_ACTIVE_VIEW', payload: 'create'});
  };

  if (activeAction === 'edit' && selectedMaintenance) {
    const maintenance = maintenances.find((m) => m.id === selectedMaintenance);
    return (
      <MaintenanceEditForm
        maintenance={maintenance!}
        onCancel={dismissActiveAction}
      />
    );
  }

  if (activeAction === 'link' && selectedMaintenance) {
    return (
      <LinkDevice
        selectedMaintenance={selectedMaintenance}
        onCancel={dismissActiveAction}
      />
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.actionBar}>
        <SearchInput onChange={table.setGlobalFilter} />
        <Button variant='secondary' className={styles.actionCreate} onClick={setActiveViewToCreateMaintenance}>
          <>
            <i className='fas fa-plus' />
            <span className={styles.buttonText}>{intl.formatMessage({id: 'sharedAdd'})}</span>
          </>
        </Button>
      </div>

      <Table
        table={table}
        theme={theme}
        emptyMessage={intl.formatMessage({id: 'sharedNoDataText'})}
      />

      <TablePageControls
        pageCount={table.getPageCount()}
        pageIndex={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        onPageSizeChange={(newPageSize) => {
          if (newPageSize === -1) {
            table.setPageSize(data.length);
            setPagination((prev) => ({...prev, pageSize: -1}));
          } else {
            table.setPageSize(newPageSize);
            setPagination((prev) => ({...prev, pageSize: newPageSize}));
          }
        }}
        setPageIndex={table.setPageIndex}
        getCanPreviousPage={table.getCanPreviousPage}
        getCanNextPage={table.getCanNextPage}
        previousPage={table.previousPage}
        nextPage={table.nextPage}
      />

      <Dialog
        open={isDeleteActionActive}
        title={intl.formatMessage({id: 'maintenanceRemoveTitle'})}
        description={intl.formatMessage({id: 'maintenanceRemoveDescription'})}
        confirmationText={intl.formatMessage({id: 'sharedRemove'})}
        onConfirm={onConfirmDelete}
        cancelationText={intl.formatMessage({id: 'sharedCancel'})}
        onCancel={dismissActiveAction}
      />
    </div>
  );
};

export default MaintenanceRegisteredTab;
